import nx from '../../assets/env/nx.jpg';
import ny from '../../assets/env/ny.jpg';
import nz from '../../assets/env/nz.jpg';
import px from '../../assets/env/px.jpg';
import py from '../../assets/env/py.jpg';
import pz from '../../assets/env/pz.jpg';

import {
    LegacySourceTexture,
    type SourceTextureWebGLUploadResult,
    TextureMipmapGroup,
    type WebGLTextureUploadCtx,
    createImgByUrl,
} from './Texture';
import { TextureDimension, TextureViewDimension } from './types';
import { Texture2DCommonLayer } from './Texture2D';
import { ContentBridge } from '../../ContentAPI';
import { singleton } from '../../utils/Utils';
import type { Serializer, Deserializer } from '../../utils/Serialization';

/**
 * Mipmapped texture data for one cube-map side.
 */
export type TextureCubeSide = TextureMipmapGroup<Texture2DCommonLayer>;

const urls = [px, nx, py, ny, pz, nz];
const defaultTextureCube = singleton(createDefaultTextureCube);
export function createDefaultTextureCube() {
    return TextureCube.fromFirstLayerArray([
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
        Texture2DCommonLayer.create(new Uint8Array([233, 233, 233, 255]), 1, 1),
    ]);
}

let defaultEnvMap: Promise<TextureCube> | undefined;
export class TextureCube extends LegacySourceTexture {
    static get default() {
        return defaultTextureCube();
    }

    className() {
        return 'TextureCube';
    }

    static fromImageArray(arr: HTMLImageElement[]) {
        return TextureCube.fromArray(arr.map(image => TextureMipmapGroup.create(Texture2DCommonLayer.create(image))));
    }

    static async fromImageArrayAsync(arr: HTMLImageElement[]) {
        return TextureCube.fromArray(
            await Promise.all(
                arr.map(async image => TextureMipmapGroup.create(await Texture2DCommonLayer.createAsync(image))),
            ),
        );
    }

    static fromFirstLayerArray(arr: Texture2DCommonLayer[]) {
        return TextureCube.fromArray(arr.map(image => TextureMipmapGroup.create(image)));
    }

    static fromArray(arr: Array<TextureMipmapGroup<Texture2DCommonLayer>>) {
        return new TextureCube(arr[0], arr[1], arr[2], arr[3], arr[4], arr[5]);
    }

    static defaultEnvMap: TextureCube;
    static getDefaultEnvMap(): Promise<TextureCube> {
        if (defaultEnvMap === undefined) {
            defaultEnvMap = TextureCube.fromImageArrayAsync(urls.map(url => createImgByUrl(url))).then(
                t => (TextureCube.defaultEnvMap = t),
            );
        }
        return defaultEnvMap;
    }

    constructor(
        public px: TextureCubeSide,
        public nx: TextureCubeSide,
        public py: TextureCubeSide,
        public ny: TextureCubeSide,
        public pz: TextureCubeSide,
        public nz: TextureCubeSide,
    ) {
        super(TextureDimension.D2, TextureViewDimension.Cube, false);
        ContentBridge.textureCreate(this);
        this.storageDescriptor.flipY = false;
        this.syncBase();
        this.syncData();
    }

    syncData() {
        this.px.syncData(this, 0);
        this.nx.syncData(this, 1);
        this.py.syncData(this, 2);
        this.ny.syncData(this, 3);
        this.pz.syncData(this, 4);
        this.nz.syncData(this, 5);
    }

    /**
     * @internal
     */
    uploadWebGLImpl(
        ctx: WebGLTextureUploadCtx,
        disableCustomMipmap: boolean,
        needPOT: boolean,
    ): SourceTextureWebGLUploadResult {
        const result = [
            this.nx.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_NEGATIVE_X, disableCustomMipmap, needPOT),
            this.px.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_POSITIVE_X, disableCustomMipmap, needPOT),
            this.ny.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_NEGATIVE_Y, disableCustomMipmap, needPOT),
            this.py.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_POSITIVE_Y, disableCustomMipmap, needPOT),
            this.nz.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_NEGATIVE_Z, disableCustomMipmap, needPOT),
            this.pz.uploadWebGL(ctx, ctx.gl.TEXTURE_CUBE_MAP_POSITIVE_Z, disableCustomMipmap, needPOT),
        ];
        let byteSize = 0;
        result.forEach(r => (byteSize += r.byteSize));
        return {
            byteSize,
            is_pot: result[0].is_pot,
            has_uploaded_custom_mipmap: result[0].has_uploaded_custom_mipmap,
        };
    }

    /**
     * @internal
     */
    canAutoGenerateMipmap(): boolean {
        return true;
    }

    protected getAutoGeneratedMipmapByteSize() {
        const main = this.nx.main;
        if (main) {
            return main.getAutoGeneratedMipmapFromCurrentLevel() * 6;
        }
        return 0;
    }

    serialize(ctx: Serializer<any>) {
        super.serialize(ctx);
        ctx.putRaw('px', this.px.main.ser());
        ctx.putRaw('py', this.py.main.ser());
        ctx.putRaw('pz', this.pz.main.ser());
        ctx.putRaw('nx', this.nx.main.ser());
        ctx.putRaw('ny', this.ny.main.ser());
        ctx.putRaw('nz', this.nz.main.ser());
    }

    async deserialize(ctx: Deserializer) {
        super.deserialize(ctx);
        const px = ctx.readRaw('px');
        const py = ctx.readRaw('py');
        const pz = ctx.readRaw('pz');
        const nx = ctx.readRaw('nx');
        const ny = ctx.readRaw('ny');
        const nz = ctx.readRaw('nz');
        if (px) {
            const image = createImgByUrl(px);
            this.px.main = await Texture2DCommonLayer.createAsync(image);
        }
        if (py) {
            const image = createImgByUrl(py);
            this.py.main = await Texture2DCommonLayer.createAsync(image);
        }
        if (pz) {
            const image = createImgByUrl(pz);
            this.pz.main = await Texture2DCommonLayer.createAsync(image);
        }
        if (nx) {
            const image = createImgByUrl(nx);
            this.nx.main = await Texture2DCommonLayer.createAsync(image);
        }
        if (ny) {
            const image = createImgByUrl(ny);
            this.ny.main = await Texture2DCommonLayer.createAsync(image);
        }
        if (nz) {
            const image = createImgByUrl(nz);
            this.nz.main = await Texture2DCommonLayer.createAsync(image);
        }
        this.syncData();
    }
}
